/* Financial Tools Suite — Shared Locale, Currency, Navigation & Search Manager */

const COUNTRIES = {
  IN: {
    id: 'IN',
    name: 'India',
    flag: '🇮🇳',
    locale: 'en-IN',
    currency: 'INR',
    sipDefaults: { investment: 5000, interest: 12, years: 10, stepUp: 0 },
    sipRanges: {
      investment: { min: 500, max: 200000, step: 500 },
      interest: { min: 1, max: 30, step: 0.5 },
      years: { min: 1, max: 40, step: 1 }
    },
    defaults: {
      propertyPrice: 10000000,
      downPayment: 20,
      buyingCosts: 5,
      loanRate: 8.5,
      loanTenure: 20,
      appreciation: 6,
      maintenance: 1,
      monthlyRent: 30000,
      rentEscalation: 7,
      equityReturn: 12,
      comparisonYears: 20,
      taxRate: 30,
      hraExemption: 0,
    },
    ranges: {
      propertyPrice: { min: 2000000, max: 50000000, step: 100000 },
      monthlyRent: { min: 5000, max: 10000000, step: 1000 },
      hraExemption: { min: 0, max: 10000000, step: 10000 },
    },
    tax: {
      interestCap: 200000,
      principalCap: 150000,
      note: 'Sec 24(b): up to ₹2L interest deduction. Sec 80C: up to ₹1.5L principal. Applied when enabled.',
      regimeLabel: 'Include tax benefits (Old Regime)',
      hraLabel: 'Annual HRA exemption (renter)',
    },
    regions: ['IN'],
  },
  US: {
    id: 'US',
    name: 'United States',
    flag: '🇺🇸',
    locale: 'en-US',
    currency: 'USD',
    sipDefaults: { investment: 500, interest: 10, years: 10, stepUp: 0 },
    sipRanges: {
      investment: { min: 50, max: 20000, step: 50 },
      interest: { min: 1, max: 30, step: 0.5 },
      years: { min: 1, max: 40, step: 1 }
    },
    defaults: {
      propertyPrice: 450000,
      downPayment: 20,
      buyingCosts: 3,
      loanRate: 6.8,
      loanTenure: 30,
      appreciation: 4,
      maintenance: 1.5,
      monthlyRent: 2200,
      rentEscalation: 4,
      equityReturn: 8,
      comparisonYears: 20,
      taxRate: 24,
      hraExemption: 0,
    },
    ranges: {
      propertyPrice: { min: 100000, max: 3000000, step: 10000 },
      monthlyRent: { min: 500, max: 15000, step: 100 },
      hraExemption: { min: 0, max: 50000, step: 1000 },
    },
    tax: {
      interestCap: 10000,
      principalCap: 0,
      note: 'Mortgage interest deduction (simplified): up to $10,000 SALT + interest cap. Applied when enabled.',
      regimeLabel: 'Include tax benefits',
      hraLabel: 'Annual housing tax deduction (renter)',
    },
    regions: ['US'],
  },
  GB: {
    id: 'GB',
    name: 'United Kingdom',
    flag: '🇬🇧',
    locale: 'en-GB',
    currency: 'GBP',
    sipDefaults: { investment: 300, interest: 8, years: 10, stepUp: 0 },
    sipRanges: {
      investment: { min: 25, max: 10000, step: 25 },
      interest: { min: 1, max: 30, step: 0.5 },
      years: { min: 1, max: 40, step: 1 }
    },
    defaults: {
      propertyPrice: 350000,
      downPayment: 15,
      buyingCosts: 4,
      loanRate: 5.2,
      loanTenure: 25,
      appreciation: 3.5,
      maintenance: 1,
      monthlyRent: 1500,
      rentEscalation: 4,
      equityReturn: 7,
      comparisonYears: 20,
      taxRate: 40,
      hraExemption: 0,
    },
    ranges: {
      propertyPrice: { min: 100000, max: 2500000, step: 10000 },
      monthlyRent: { min: 400, max: 10000, step: 50 },
      hraExemption: { min: 0, max: 30000, step: 500 },
    },
    tax: {
      interestCap: 0,
      principalCap: 0,
      note: 'UK mortgage interest relief is limited for landlords. Toggle for simplified stamp duty savings estimate.',
      regimeLabel: 'Include tax benefits (simplified)',
      hraLabel: 'Annual rent relief estimate',
    },
    regions: ['GB', 'UK'],
  },
  CA: {
    id: 'CA',
    name: 'Canada',
    flag: '🇨🇦',
    locale: 'en-CA',
    currency: 'CAD',
    sipDefaults: { investment: 500, interest: 8, years: 10, stepUp: 0 },
    sipRanges: {
      investment: { min: 50, max: 15000, step: 50 },
      interest: { min: 1, max: 30, step: 0.5 },
      years: { min: 1, max: 40, step: 1 }
    },
    defaults: {
      propertyPrice: 650000,
      downPayment: 20,
      buyingCosts: 3,
      loanRate: 5.5,
      loanTenure: 25,
      appreciation: 4,
      maintenance: 1.2,
      monthlyRent: 2400,
      rentEscalation: 3.5,
      equityReturn: 7,
      comparisonYears: 20,
      taxRate: 33,
      hraExemption: 0,
    },
    ranges: {
      propertyPrice: { min: 200000, max: 4000000, step: 25000 },
      monthlyRent: { min: 600, max: 12000, step: 100 },
      hraExemption: { min: 0, max: 40000, step: 1000 },
    },
    tax: {
      interestCap: 5000,
      principalCap: 0,
      note: 'First-time buyer incentives and mortgage interest (simplified). Applied when enabled.',
      regimeLabel: 'Include tax benefits',
      hraLabel: 'Annual renter tax credit estimate',
    },
    regions: ['CA'],
  },
  AU: {
    id: 'AU',
    name: 'Australia',
    flag: '🇦🇺',
    locale: 'en-AU',
    currency: 'AUD',
    sipDefaults: { investment: 600, interest: 9, years: 10, stepUp: 0 },
    sipRanges: {
      investment: { min: 50, max: 20000, step: 50 },
      interest: { min: 1, max: 30, step: 0.5 },
      years: { min: 1, max: 40, step: 1 }
    },
    defaults: {
      propertyPrice: 850000,
      downPayment: 20,
      buyingCosts: 5,
      loanRate: 6.2,
      loanTenure: 30,
      appreciation: 5,
      maintenance: 1,
      monthlyRent: 2600,
      rentEscalation: 4,
      equityReturn: 8,
      comparisonYears: 20,
      taxRate: 37,
      hraExemption: 0,
    },
    ranges: {
      propertyPrice: { min: 300000, max: 5000000, step: 25000 },
      monthlyRent: { min: 800, max: 15000, step: 100 },
      hraExemption: { min: 0, max: 50000, step: 1000 },
    },
    tax: {
      interestCap: 0,
      principalCap: 0,
      note: 'Negative gearing and stamp duty concessions (simplified). Applied when enabled.',
      regimeLabel: 'Include tax benefits (simplified)',
      hraLabel: 'Annual renter rebate estimate',
    },
    regions: ['AU'],
  },
  DE: {
    id: 'DE',
    name: 'Germany (EUR)',
    flag: '🇩🇪',
    locale: 'de-DE',
    currency: 'EUR',
    sipDefaults: { investment: 300, interest: 7, years: 10, stepUp: 0 },
    sipRanges: {
      investment: { min: 25, max: 10000, step: 25 },
      interest: { min: 1, max: 30, step: 0.5 },
      years: { min: 1, max: 40, step: 1 }
    },
    defaults: {
      propertyPrice: 420000,
      downPayment: 20,
      buyingCosts: 6,
      loanRate: 3.8,
      loanTenure: 25,
      appreciation: 3,
      maintenance: 1.5,
      monthlyRent: 1300,
      rentEscalation: 3,
      equityReturn: 6,
      comparisonYears: 20,
      taxRate: 42,
      hraExemption: 0,
    },
    ranges: {
      propertyPrice: { min: 150000, max: 2500000, step: 10000 },
      monthlyRent: { min: 400, max: 8000, step: 50 },
      hraExemption: { min: 0, max: 30000, step: 500 },
    },
    tax: {
      interestCap: 0,
      principalCap: 0,
      note: 'German Eigenheimzulage and interest deductions (simplified). Applied when enabled.',
      regimeLabel: 'Include tax benefits (simplified)',
      hraLabel: 'Annual renter allowance estimate',
    },
    regions: ['DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'AT', 'PT', 'IE', 'FI', 'GR', 'LU'],
  },
  JP: {
    id: 'JP',
    name: 'Japan',
    flag: '🇯🇵',
    locale: 'ja-JP',
    currency: 'JPY',
    sipDefaults: { investment: 30000, interest: 6, years: 10, stepUp: 0 },
    sipRanges: {
      investment: { min: 3000, max: 1000000, step: 1000 },
      interest: { min: 1, max: 30, step: 0.5 },
      years: { min: 1, max: 40, step: 1 }
    },
    defaults: {
      propertyPrice: 45000000,
      downPayment: 20,
      buyingCosts: 6,
      loanRate: 1.2,
      loanTenure: 35,
      appreciation: 1.5,
      maintenance: 1,
      monthlyRent: 120000,
      rentEscalation: 2,
      equityReturn: 5,
      comparisonYears: 20,
      taxRate: 33,
      hraExemption: 0,
    },
    ranges: {
      propertyPrice: { min: 10000000, max: 150000000, step: 1000000 },
      monthlyRent: { min: 50000, max: 500000, step: 5000 },
      hraExemption: { min: 0, max: 2000000, step: 50000 },
    },
    tax: {
      interestCap: 0,
      principalCap: 0,
      note: 'Japanese housing loan deduction (simplified, Year 1 only estimate). Applied when enabled.',
      regimeLabel: 'Include tax benefits (simplified)',
      hraLabel: 'Annual renter deduction estimate',
    },
    regions: ['JP'],
  },
  SG: {
    id: 'SG',
    name: 'Singapore',
    flag: '🇸🇬',
    locale: 'en-SG',
    currency: 'SGD',
    sipDefaults: { investment: 800, interest: 7, years: 10, stepUp: 0 },
    sipRanges: {
      investment: { min: 100, max: 20000, step: 100 },
      interest: { min: 1, max: 30, step: 0.5 },
      years: { min: 1, max: 40, step: 1 }
    },
    defaults: {
      propertyPrice: 1200000,
      downPayment: 25,
      buyingCosts: 4,
      loanRate: 3.5,
      loanTenure: 25,
      appreciation: 3,
      maintenance: 0.8,
      monthlyRent: 3500,
      rentEscalation: 3,
      equityReturn: 6,
      comparisonYears: 20,
      taxRate: 22,
      hraExemption: 0,
    },
    ranges: {
      propertyPrice: { min: 500000, max: 6000000, step: 50000 },
      monthlyRent: { min: 1000, max: 20000, step: 100 },
      hraExemption: { min: 0, max: 50000, step: 1000 },
    },
    tax: {
      interestCap: 0,
      principalCap: 0,
      note: 'CPF usage and property tax rebates (simplified). Applied when enabled.',
      regimeLabel: 'Include tax benefits (simplified)',
      hraLabel: 'Annual renter relief estimate',
    },
    regions: ['SG'],
  },
  AE: {
    id: 'AE',
    name: 'UAE',
    flag: '🇦🇪',
    locale: 'en-AE',
    currency: 'AED',
    sipDefaults: { investment: 2000, interest: 8, years: 10, stepUp: 0 },
    sipRanges: {
      investment: { min: 200, max: 50000, step: 200 },
      interest: { min: 1, max: 30, step: 0.5 },
      years: { min: 1, max: 40, step: 1 }
    },
    defaults: {
      propertyPrice: 1500000,
      downPayment: 25,
      buyingCosts: 4,
      loanRate: 4.5,
      loanTenure: 25,
      appreciation: 5,
      maintenance: 1,
      monthlyRent: 8000,
      rentEscalation: 5,
      equityReturn: 7,
      comparisonYears: 20,
      taxRate: 0,
      hraExemption: 0,
    },
    ranges: {
      propertyPrice: { min: 500000, max: 15000000, step: 50000 },
      monthlyRent: { min: 2000, max: 50000, step: 500 },
      hraExemption: { min: 0, max: 100000, step: 5000 },
    },
    tax: {
      interestCap: 0,
      principalCap: 0,
      note: 'No personal income tax in UAE. Toggle has minimal effect unless you assign a hypothetical rate above.',
      regimeLabel: 'Include tax benefits (optional)',
      hraLabel: 'Annual housing allowance estimate',
    },
    regions: ['AE'],
  },
  CN: {
    id: 'CN',
    name: 'China',
    flag: '🇨🇳',
    locale: 'zh-CN',
    currency: 'CNY',
    sipDefaults: { investment: 2000, interest: 7, years: 10, stepUp: 0 },
    sipRanges: {
      investment: { min: 200, max: 50000, step: 200 },
      interest: { min: 1, max: 30, step: 0.5 },
      years: { min: 1, max: 40, step: 1 }
    },
    defaults: {
      propertyPrice: 3000000,
      downPayment: 30,
      buyingCosts: 3,
      loanRate: 4.2,
      loanTenure: 30,
      appreciation: 3,
      maintenance: 1,
      monthlyRent: 8000,
      rentEscalation: 3,
      equityReturn: 7,
      comparisonYears: 20,
      taxRate: 25,
      hraExemption: 0,
    },
    ranges: {
      propertyPrice: { min: 500000, max: 20000000, step: 100000 },
      monthlyRent: { min: 2000, max: 50000, step: 500 },
      hraExemption: { min: 0, max: 200000, step: 5000 },
    },
    tax: {
      interestCap: 0,
      principalCap: 0,
      note: 'Housing fund (公积金) and mortgage deductions (simplified). Applied when enabled.',
      regimeLabel: 'Include tax benefits (simplified)',
      hraLabel: 'Annual rent deduction estimate',
    },
    regions: ['CN'],
  },
};

const REGION_TO_COUNTRY = Object.values(COUNTRIES).reduce((map, country) => {
  country.regions.forEach((region) => {
    map[region] = country.id;
  });
  return map;
}, {});

const LANGUAGES = {
  en: { code: 'en', name: 'English', flag: '🇬🇧', dir: 'ltr' },
  hi: { code: 'hi', name: 'हिन्दी (Hindi)', flag: '🇮🇳', dir: 'ltr' },
  de: { code: 'de', name: 'Deutsch (German)', flag: '🇩🇪', dir: 'ltr' },
  ja: { code: 'ja', name: '日本語 (Japanese)', flag: '🇯🇵', dir: 'ltr' },
  fr: { code: 'fr', name: 'Français (French)', flag: '🇫🇷', dir: 'ltr' },
  es: { code: 'es', name: 'Español (Spanish)', flag: '🇪🇸', dir: 'ltr' },
  zh: { code: 'zh', name: '中文 (Chinese)', flag: '🇨🇳', dir: 'ltr' },
  ar: { code: 'ar', name: 'العربية (Arabic)', flag: '🇦🇪', dir: 'rtl' },
};

const TRANSLATIONS = {
  en: {
    "nav.home": "Home",
    "nav.finance": "Financial Decision Calculators",
    "nav.dev": "Developer Productivity Utilities",
    "search.placeholder": "Search any utility… (or press /)",
    "btn.exportCsv": "Export CSV",
    "btn.launch": "Launch Utility",
    "home.heroTitle": "Free Financial Calculators & Developer Productivity Utilities",
    "home.heroSub": "JustOnlyOneThing provides 22 free, client-side tools for calculating investment compounding, mortgage vs. rent equity, loan EMIs, startup runways, JSON formatting, regular expressions, Base64 encoding, and JWT tokens directly in your browser with zero data tracking.",
    "breakeven.fixedCosts": "Total monthly fixed overhead costs",
    "breakeven.sellingPrice": "Selling price per unit",
    "breakeven.variableCost": "Variable cost per unit",
    "breakeven.expectedUnits": "Expected sales volume (Units)",
    "breakeven.recurringIncome": "Monthly recurring base income",
    "breakeven.growthRate": "Monthly income growth rate (%)",
    "profitmargin.revenue": "Total sales revenue",
    "profitmargin.cogs": "Cost of Goods Sold (COGS)",
    "profitmargin.opex": "Operating expenses (OpEx)",
    "profitmargin.taxInt": "Taxes & interest expenses",
    "workingcapital.cash": "Cash & bank balances",
    "workingcapital.ar": "Accounts receivable (Debtors)",
    "workingcapital.inv": "Inventory & stock",
    "workingcapital.otherCa": "Other current assets",
    "workingcapital.ap": "Accounts payable (Creditors)",
    "workingcapital.stLoans": "Short-term loans & overdrafts",
    "workingcapital.otherCl": "Other current liabilities",
  },
  hi: {
    "nav.home": "होम",
    "nav.finance": "वित्त उपयोगिताएं",
    "search.placeholder": "किसी भी टूल को खोजें… (या दबाएं /)",
    "btn.exportCsv": "सीएसवी निर्यात",
    "btn.launch": "उपयोगिता शुरू करें",
    "breakeven.fixedCosts": "कुल मासिक निश्चित लागत",
    "breakeven.sellingPrice": "प्रति इकाई बिक्री मूल्य",
    "breakeven.variableCost": "प्रति इकाई परिवर्तनीय लागत",
    "breakeven.expectedUnits": "अपेक्षित बिक्री मात्रा (इकाइयां)",
    "breakeven.recurringIncome": "मासिक आवर्ती मूल आय",
    "breakeven.growthRate": "मासिक आय वृद्धि दर (%)",
    "profitmargin.revenue": "कुल बिक्री राजस्व",
    "profitmargin.cogs": "बेचे गए माल की लागत (COGS)",
    "profitmargin.opex": "परिचालन व्यय (OpEx)",
    "profitmargin.taxInt": "कर और ब्याज व्यय",
    "workingcapital.cash": "नकद और बैंक शेष",
    "workingcapital.ar": "प्राप्य खाते (देनदार)",
    "workingcapital.inv": "इन्वेंट्री और स्टॉक",
    "workingcapital.otherCa": "अन्य वर्तमान संपत्तियां",
    "workingcapital.ap": "देय खाते (लेनदार)",
    "workingcapital.stLoans": "अल्पकालिक ऋण और ओवरड्राफ्ट",
    "workingcapital.otherCl": "अन्य वर्तमान देनदारियां",
  },
  de: {
    "nav.home": "Startseite",
    "nav.finance": "Finanzrechner",
    "search.placeholder": "Rechner suchen… (oder / drücken)",
    "btn.exportCsv": "CSV Exportieren",
    "btn.launch": "Rechner Öffnen",
    "breakeven.fixedCosts": "Monatliche Gesamtfixkosten",
    "breakeven.sellingPrice": "Verkaufspreis pro Einheit",
    "breakeven.variableCost": "Variable Kosten pro Einheit",
    "breakeven.expectedUnits": "Erwartetes Verkaufsvolumen (Einheiten)",
    "breakeven.recurringIncome": "Wiederkehrendes Monatseinkommen",
    "breakeven.growthRate": "Monatliche Einkommenswachstumsrate (%)",
    "profitmargin.revenue": "Gesamter Verkaufserlös",
    "profitmargin.cogs": "Herstellungskosten (COGS)",
    "profitmargin.opex": "Betriebsausgaben (OpEx)",
    "profitmargin.taxInt": "Steuern und Zinsaufwendungen",
    "workingcapital.cash": "Bargeld und Bankguthaben",
    "workingcapital.ar": "Forderungen aus Lieferungen",
    "workingcapital.inv": "Warenbestand und Lager",
    "workingcapital.otherCa": "Sonstige Umlaufvermögen",
    "workingcapital.ap": "Verbindlichkeiten aus Lieferungen",
    "workingcapital.stLoans": "Kurzfristige Kredite",
    "workingcapital.otherCl": "Sonstige kurzfristige Verbindlichkeiten",
  },
  ja: {
    "nav.home": "ホーム",
    "nav.finance": "Financial Utilities",
    "search.placeholder": "Search any utility… (or press /)",
    "btn.exportCsv": "CSVエクスポート",
    "btn.launch": "Launch Utility",
    "breakeven.fixedCosts": "月間固定費合計",
    "breakeven.sellingPrice": "1単位あたりの販売価格",
    "breakeven.variableCost": "1単位あたりの変動費",
    "breakeven.expectedUnits": "予想販売数量（単位）",
    "breakeven.recurringIncome": "月間継続基本収入",
    "breakeven.growthRate": "月間収入成長率 (%)",
    "profitmargin.revenue": "売上高合計",
    "profitmargin.cogs": "売上原価（COGS）",
    "profitmargin.opex": "販売管理費（OpEx）",
    "profitmargin.taxInt": "税金および利息費用",
    "workingcapital.cash": "現金および預金",
    "workingcapital.ar": "売掛金（売上債権）",
    "workingcapital.inv": "棚卸資産・在庫",
    "workingcapital.otherCa": "その他流動資産",
    "workingcapital.ap": "買掛金（仕入債務）",
    "workingcapital.stLoans": "短期借入金・当座貸越",
    "workingcapital.otherCl": "その他流動負債",
  },
  ar: {
    "nav.home": "الرئيسية",
    "nav.finance": "الأدوات المالية",
    "search.placeholder": "البحث في الأدوات… (أو اضغط /)",
    "btn.exportCsv": "تصدير CSV",
    "btn.launch": "تشغيل الأداة",
    "breakeven.fixedCosts": "إجمالي التكاليف الثابتة الشهرية",
    "breakeven.sellingPrice": "سعر البيع للوحدة",
    "breakeven.variableCost": "التكلفة المتغيرة للوحدة",
    "breakeven.expectedUnits": "حجم المبيعات المتوقع (وحدات)",
    "breakeven.recurringIncome": "الدخل الأساسي الشهري المتكرر",
    "breakeven.growthRate": "معدل نمو الدخل الشهري (%)",
    "profitmargin.revenue": "إجمالي إيرادات المبيعات",
    "profitmargin.cogs": "تكلفة البضاعة المباعة (COGS)",
    "profitmargin.opex": "المصاريف التشغيلية (OpEx)",
    "profitmargin.taxInt": "مصاريف الضرائب والفوائد",
    "workingcapital.cash": "النقد والأرصدة المصرفية",
    "workingcapital.ar": "الحسابات المدينة (العملاء)",
    "workingcapital.inv": "المخزون والبضائع",
    "workingcapital.otherCa": "الأصول المتداولة الأخرى",
    "workingcapital.ap": "الحسابات الدائنة (الموردين)",
    "workingcapital.stLoans": "القروض قصيرة الأجل والسحب على المكشوف",
    "workingcapital.otherCl": "الالتزامات المتداولة الأخرى",
  },
  fr: {
    "nav.home": "Accueil",
    "nav.finance": "Utilitaires Financiers",
    "search.placeholder": "Rechercher un outil… (ou appuyez sur /)",
    "btn.exportCsv": "Exporter CSV",
    "btn.launch": "Lancer l'outil",
    "breakeven.fixedCosts": "Frais fixes mensuels totaux",
    "breakeven.sellingPrice": "Prix de vente unitaire",
    "breakeven.variableCost": "Coût variable par unité",
    "breakeven.expectedUnits": "Volume de ventes prévu (Unités)",
    "breakeven.recurringIncome": "Revenu de base récurrent mensuel",
    "breakeven.growthRate": "Taux de croissance du revenu (%)",
    "profitmargin.revenue": "Chiffre d'affaires total",
    "profitmargin.cogs": "Coût des marchandises vendues (COGS)",
    "profitmargin.opex": "Dépenses d'exploitation (OpEx)",
    "profitmargin.taxInt": "Impôts et frais d'intérêts",
    "workingcapital.cash": "Trésorerie et solde bancaire",
    "workingcapital.ar": "Comptes clients (Créances)",
    "workingcapital.inv": "Stocks et marchandises",
    "workingcapital.otherCa": "Autres actifs circulants",
    "workingcapital.ap": "Comptes fournisseurs (Dettes)",
    "workingcapital.stLoans": "Emprunts à court terme",
    "workingcapital.otherCl": "Autres passifs circulants",
  },
  es: {
    "nav.home": "Inicio",
    "nav.finance": "Utilidades Financieras",
    "search.placeholder": "Buscar utilidad… (o presione /)",
    "btn.exportCsv": "Exportar CSV",
    "btn.launch": "Iniciar herramienta",
    "breakeven.fixedCosts": "Costos fijos mensuales totales",
    "breakeven.sellingPrice": "Precio de venta por unidad",
    "breakeven.variableCost": "Costo variable por unidad",
    "breakeven.expectedUnits": "Volumen de ventas esperado (Unidades)",
    "breakeven.recurringIncome": "Ingreso base recurrente mensual",
    "breakeven.growthRate": "Tasa de crecimiento de ingresos (%)",
    "profitmargin.revenue": "Ingresos totales de ventas",
    "profitmargin.cogs": "Costo de bienes vendidos (COGS)",
    "profitmargin.opex": "Gastos operativos (OpEx)",
    "profitmargin.taxInt": "Impuestos y gastos de intereses",
    "workingcapital.cash": "Efectivo y saldos bancarios",
    "workingcapital.ar": "Cuentas por cobrar (Deudores)",
    "workingcapital.inv": "Inventario y existencias",
    "workingcapital.otherCa": "Otros activos corrientes",
    "workingcapital.ap": "Cuentas por pagar (Acreedores)",
    "workingcapital.stLoans": "Préstamos a corto plazo",
    "workingcapital.otherCl": "Otros pasivos corrientes",
  },
  zh: {
    "nav.home": "首页",
    "nav.finance": "Financial Utilities",
    "search.placeholder": "Search any utility… (or press /)",
    "btn.exportCsv": "CSV Export",
    "btn.launch": "Launch Utility",
    "breakeven.fixedCosts": "每月固定运营成本总额",
    "breakeven.sellingPrice": "单件商品销售单价",
    "breakeven.variableCost": "单件商品变动成本",
    "breakeven.expectedUnits": "预期销售数量（件/套）",
    "breakeven.recurringIncome": "每月经常性基础收入",
    "breakeven.growthRate": "月收入增长率 (%)",
    "profitmargin.revenue": "总销售营业收入",
    "profitmargin.cogs": "销货成本 / 直接成本 (COGS)",
    "profitmargin.opex": "日常运营费用 (OpEx)",
    "profitmargin.taxInt": "税费及利息支出",
    "workingcapital.cash": "现金及银行存款",
    "workingcapital.ar": "应收账款（债权）",
    "workingcapital.inv": "存货及库存商品",
    "workingcapital.otherCa": "其他流动资产",
    "workingcapital.ap": "应付账款（债务）",
    "workingcapital.stLoans": "短期借款及透支",
    "workingcapital.otherCl": "其他流动负债",
  }
};

const LANG_STORAGE_KEY = 'homecompare-lang';
let currentLang = 'en';

function getLanguage() {
  const urlParams = new URLSearchParams(window.location.search);
  const paramLang = urlParams.get('lang');
  if (paramLang && LANGUAGES[paramLang]) return paramLang;

  try {
    const saved = localStorage.getItem(LANG_STORAGE_KEY);
    if (saved && LANGUAGES[saved]) return saved;
  } catch (e) {}

  return 'en';
}

function t(key, fallback = '') {
  const dict = TRANSLATIONS[currentLang] || TRANSLATIONS.en;
  return dict[key] || TRANSLATIONS.en[key] || fallback || key;
}

function applyI18n() {
  document.querySelectorAll('[data-i18n]').forEach((el) => {
    const key = el.getAttribute('data-i18n');
    if (key) el.textContent = t(key, el.textContent);
  });

  document.querySelectorAll('[data-i18n-placeholder]').forEach((el) => {
    const key = el.getAttribute('data-i18n-placeholder');
    if (key) el.placeholder = t(key, el.placeholder);
  });
}

function setLanguage(langCode, { persist = true } = {}) {
  currentLang = LANGUAGES[langCode] ? langCode : 'en';
  const langConfig = LANGUAGES[currentLang];

  document.documentElement.lang = currentLang;
  document.documentElement.dir = langConfig.dir;

  if (persist) {
    try {
      localStorage.setItem(LANG_STORAGE_KEY, currentLang);
    } catch (e) {}
  }

  const select = document.getElementById('langSelect');
  if (select && select.value !== currentLang) {
    select.value = currentLang;
  }

  applyI18n();

  if (persist && typeof window.gtagEvent === 'function') {
    window.gtagEvent('language_change', {
      language: currentLang,
    });
  }
}

function populateLanguageSelect() {
  const select = document.getElementById('langSelect');
  if (select) {
    const parentLabel = select.closest('.country-select-label');
    if (parentLabel) {
      parentLabel.remove();
    } else {
      select.remove();
    }
  }
}

const STORAGE_KEY = 'homecompare-country';
const THEME_STORAGE_KEY = 'homecompare-theme';

let currentCountry = COUNTRIES.IN;
let currencyFormatter = null;
let onCountryChangeListeners = [];

function cssVar(name) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function createFormatter(locale, currency) {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  });
}

function detectCountryFromLocale() {
  const candidates = [
    ...(navigator.languages || []),
    navigator.language,
    Intl.DateTimeFormat().resolvedOptions().locale,
  ].filter(Boolean);

  for (const tag of candidates) {
    const parts = tag.split('-');
    const region = parts[parts.length - 1]?.toUpperCase();
    if (region && REGION_TO_COUNTRY[region]) {
      return { id: REGION_TO_COUNTRY[region], locale: tag };
    }
  }

  return { id: 'IN', locale: 'en-IN' };
}

function fmt(n) {
  if (!currencyFormatter) {
    currencyFormatter = createFormatter(currentCountry.locale, currentCountry.currency);
  }
  return currencyFormatter.format(Math.round(n));
}

function fmtPct(n) {
  return `${n}%`;
}

function fmtCompact(n) {
  if (!currencyFormatter) {
    currencyFormatter = createFormatter(currentCountry.locale, currentCountry.currency);
  }
  const abs = Math.abs(n);
  const { currency } = currentCountry;

  if (currency === 'INR') {
    if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
    if (abs >= 1e5) return `₹${(n / 1e5).toFixed(1)} L`;
    if (abs >= 1e3) return `₹${Math.round(n / 1e3)} K`;
  }

  if (currency === 'JPY') {
    if (abs >= 1e8) return `¥${(n / 1e8).toFixed(1)}億`;
    if (abs >= 1e4) return `¥${Math.round(n / 1e4)}万`;
    if (abs >= 1e3) return `¥${Math.round(n / 1e3)}K`;
  }

  const symbol = currencyFormatter.formatToParts(0).find((p) => p.type === 'currency')?.value || '$';
  if (abs >= 1e6) return `${symbol}${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${symbol}${Math.round(n / 1e3)}K`;
  return fmt(n);
}

function populateCountrySelect() {
  const select = document.getElementById('countrySelect');
  if (!select) return;
  select.innerHTML = Object.values(COUNTRIES)
    .map((c) => `<option value="${c.id}">${c.flag} ${c.name} (${c.currency})</option>`)
    .join('');
}

function setCountry(countryId, { persist = true } = {}) {
  const country = COUNTRIES[countryId] || COUNTRIES.IN;
  currentCountry = country;
  currencyFormatter = createFormatter(country.locale, country.currency);

  if (persist) {
    try {
      localStorage.setItem(STORAGE_KEY, country.id);
    } catch (e) {}
  }

  const select = document.getElementById('countrySelect');
  if (select && select.value !== country.id) {
    select.value = country.id;
  }

  onCountryChangeListeners.forEach((listener) => listener(country));

  if (persist && typeof window.gtagEvent === 'function') {
    window.gtagEvent('country_change', {
      country_code: country.id,
      currency: country.currency,
      locale: country.locale,
    });
  }
}

function initCountry(onChanged) {
  if (onChanged) onCountryChangeListeners.push(onChanged);
  populateCountrySelect();

  let saved = null;
  try {
    saved = localStorage.getItem(STORAGE_KEY);
  } catch (e) {}

  if (saved && COUNTRIES[saved]) {
    setCountry(saved, { persist: false });
  } else {
    const { id } = detectCountryFromLocale();
    setCountry(id, { persist: false });
  }

  const select = document.getElementById('countrySelect');
  if (select) {
    select.addEventListener('change', (e) => {
      setCountry(e.target.value);
    });
  }
}

function updateMetaThemeColor(theme) {
  const metaTheme = document.querySelector('meta[name="theme-color"]');
  if (metaTheme) {
    metaTheme.setAttribute('content', theme === 'light' ? '#ffffff' : '#0f172a');
  }
}

function toggleTheme() {
  const root = document.documentElement;
  const next = root.dataset.theme === 'light' ? 'dark' : 'light';
  root.dataset.theme = next;
  updateMetaThemeColor(next);
  try {
    localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch (e) {}

  if (typeof window.gtagEvent === 'function') {
    window.gtagEvent('theme_change', { theme: next });
  }
}

function initTheme() {
  const root = document.documentElement;
  const current = root.dataset.theme || 'dark';
  updateMetaThemeColor(current);
  const toggleBtn = document.getElementById('themeToggle');
  if (toggleBtn) {
    toggleBtn.addEventListener('click', toggleTheme);
  }
}

function renderBreadcrumbs() {
  const container = document.getElementById('breadcrumbsNav');
  if (!container) return;

  const currentPath = window.location.pathname.toLowerCase();
  let crumbs = [{ label: 'Home', url: '/' }];

  if (currentPath.includes('about')) {
    crumbs.push({ label: 'About Us', url: '/about/' });
  } else if (currentPath === '/services' || currentPath === '/services/' || currentPath.endsWith('/services/index.html')) {
    crumbs.push({ label: 'Services & Tools Directory', url: '/services/' });
  } else if (currentPath.includes('why-developers-need-financial-discipline')) {
    crumbs.push({ label: 'Blog', url: '/blog/' });
    crumbs.push({ label: 'The Symmetry of Code and Capital', url: '/blog/why-developers-need-financial-discipline/' });
  } else if (currentPath === '/blog' || currentPath === '/blog/' || currentPath.endsWith('/blog/index.html')) {
    crumbs.push({ label: 'Blog', url: '/blog/' });
  } else if (currentPath.includes('rent-vs-buy')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'Rent vs Own House', url: '/services/rent-vs-buy/' });
  } else if (currentPath.includes('sip-calculator')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'SIP Wealth Calculator', url: '/services/sip-calculator/' });
  } else if (currentPath.includes('dscr-calculator')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'DSCR Calculator', url: '/services/dscr-calculator/' });
  } else if (currentPath.includes('compounding-calculator')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'Compounding Calculator', url: '/services/compounding-calculator/' });
  } else if (currentPath.includes('cagr-calculator')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'CAGR Calculator', url: '/services/cagr-calculator/' });
  } else if (currentPath.includes('swp-calculator')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'SWP Calculator', url: '/services/swp-calculator/' });
  } else if (currentPath.includes('simple-interest-calculator')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'Simple Interest Calculator', url: '/services/simple-interest-calculator/' });
  } else if (currentPath.includes('interest-free-home-loan')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'Interest-Free Home Loan', url: '/services/interest-free-home-loan/' });
  } else if (currentPath.includes('emi-calculator')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'EMI Calculator', url: '/services/emi-calculator/' });
  } else if (currentPath.includes('home-loan-eligibility')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'Home Loan Eligibility', url: '/services/home-loan-eligibility/' });
  } else if (currentPath.includes('startup-burn-rate')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'Startup Burn Rate', url: '/services/startup-burn-rate/' });
  } else if (currentPath.includes('rental-yield')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'Rental Yield & Income', url: '/services/rental-yield/' });
  } else if (currentPath.includes('net-worth-calculator')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'Net Worth Calculator', url: '/services/net-worth-calculator/' });
  } else if (currentPath.includes('break-even-calculator')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'Break-Even Calculator', url: '/services/break-even-calculator/' });
  } else if (currentPath.includes('profit-margin-calculator')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'Profit Margin Calculator', url: '/services/profit-margin-calculator/' });
  } else if (currentPath.includes('working-capital-calculator')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'Working Capital Calculator', url: '/services/working-capital-calculator/' });
  } else if (currentPath.includes('json-editor')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'JSON Editor Online', url: '/services/json-editor/' });
  } else if (currentPath.includes('base64-encoder-decoder') || currentPath.includes('base64')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'Base64 Encoder & Decoder', url: '/services/base64-encoder-decoder/' });
  } else if (currentPath.includes('regex-generator') || currentPath.includes('regex')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'RegEx Validator & Tester', url: '/services/regex-validator/' });
  } else if (currentPath.includes('jwt-token-inspector') || currentPath.includes('jwt')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'JWT Token Inspector & Debugger', url: '/services/jwt-token-inspector/' });
  } else if (currentPath.includes('cron-expression-parser') || currentPath.includes('cron')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'Cron Expression Parser & Schedule Generator', url: '/services/cron-expression-parser/' });
  } else if (currentPath.includes('hash-cipher-generator') || currentPath.includes('hash')) {
    crumbs.push({ label: 'Services', url: '/services/' });
    crumbs.push({ label: 'Hash & Cipher Generator', url: '/services/hash-cipher-generator/' });
  } else {
    return; // Don't show breadcrumbs on homepage
  }

  container.innerHTML = `
    <ol class="breadcrumbs-list">
      ${crumbs
        .map((crumb, i) => {
          const isLast = i === crumbs.length - 1;
          if (isLast) {
            return `<li class="breadcrumb-item active" aria-current="page">${crumb.label}</li>`;
          }
          return `<li class="breadcrumb-item"><a href="${crumb.url}">${crumb.label}</a></li>`;
        })
        .join('<li class="breadcrumb-separator">/</li>')}
    </ol>
  `;
}

function setupSearchableSection(inputId, menuId, sectionId) {
  const input = document.getElementById(inputId);
  const menu = document.getElementById(menuId);
  const section = document.getElementById(sectionId);
  if (!input || !menu || !section) return;

  const cards = Array.from(section.querySelectorAll('.tool-card'));
  const tools = cards.map((card) => {
    const title = card.querySelector('h2')?.textContent.trim() || '';
    const icon = card.querySelector('.tool-card-icon')?.textContent.trim() || '';
    const href = card.getAttribute('href') || '#';
    const desc = card.querySelector('p')?.textContent.trim() || '';
    return { card, title, icon, href, desc };
  });

  function renderMenu(items) {
    if (items.length === 0) {
      menu.innerHTML = `<li class="search-menu-empty">No matching utility found</li>`;
      return;
    }
    menu.innerHTML = items
      .map(
        (t) => `
      <li class="search-menu-item">
        <a href="${t.href}">
          <span class="menu-item-icon">${t.icon}</span>
          <span class="menu-item-title">${t.title}</span>
        </a>
      </li>`
      )
      .join('');
  }

  function filter(query) {
    const q = query.toLowerCase().trim();
    const matches = [];

    tools.forEach((t) => {
      const match = !q || t.title.toLowerCase().includes(q) || t.desc.toLowerCase().includes(q);
      t.card.style.display = match ? 'flex' : 'none';
      if (match) matches.push(t);
    });

    renderMenu(matches);
  }

  input.addEventListener('focus', () => {
    filter(input.value);
    menu.hidden = false;
  });

  input.addEventListener('input', () => {
    filter(input.value);
    menu.hidden = false;
  });

  document.addEventListener('click', (e) => {
    if (!input.contains(e.target) && !menu.contains(e.target)) {
      menu.hidden = true;
    }
  });
}

function initSearchableDropdowns() {
  setupSearchableSection('financeSearchInput', 'financeSearchMenu', 'finance');
  setupSearchableSection('developerSearchInput', 'developerSearchMenu', 'developer');
}

/* ─── Global Search Registry ─── */
const GLOBAL_TOOLS = [
  // Finance Services
  { icon: '🏠', title: 'Rent vs Own House',          cat: 'Finance',    href: '/services/rent-vs-buy/',                           tags: 'home loan emi mortgage buying' },
  { icon: '📈', title: 'SIP Wealth Calculator',       cat: 'Finance',    href: '/services/sip-calculator/?investment=3000&interest=12&years=3', tags: 'sip invest mutual fund' },
  { icon: '💹', title: 'Compounding Calculator',      cat: 'Finance',    href: '/services/compounding-calculator/',                tags: 'compound interest savings' },
  { icon: '📊', title: 'CAGR Calculator',             cat: 'Finance',    href: '/services/cagr-calculator/',                       tags: 'annual growth rate investment' },
  { icon: '💸', title: 'SWP Calculator',              cat: 'Finance',    href: '/services/swp-calculator/',                        tags: 'systematic withdrawal retirement' },
  { icon: '🪙', title: 'Simple Interest Calculator',   cat: 'Finance',    href: '/services/simple-interest-calculator/',           tags: 'simple interest flat rate savings loan' },
  { icon: '🏦', title: 'Interest-Free Home Loan',     cat: 'Finance',    href: '/services/interest-free-home-loan/',    tags: 'housing loan subsidized parallel sip offset' },
  { icon: '💳', title: 'EMI Calculator',              cat: 'Finance',    href: '/services/emi-calculator/',                        tags: 'emi loan installment repayment home loan car loan' },
  { icon: '🔍', title: 'Home Loan Eligibility',       cat: 'Finance',    href: '/services/home-loan-eligibility/',      tags: 'borrowing eligibility income foir maximum loan' },
  { icon: '🔥', title: 'Startup Burn Rate',           cat: 'Finance',    href: '/services/startup-burn-rate/',          tags: 'startup runway cash burn gross net expenses' },
  { icon: '🏢', title: 'Rental Yield & Income',       cat: 'Finance',    href: '/services/rental-yield/',               tags: 'property rental income yield real estate sec 24a' },
  { icon: '💎', title: 'Net Worth Calculator',        cat: 'Finance',    href: '/services/net-worth-calculator/',                  tags: 'assets liabilities wealth personal equity debt ratio' },
  { icon: '⚖️', title: 'Break-Even Calculator',       cat: 'Finance',    href: '/services/break-even-calculator/',                 tags: 'breakeven sales revenue fixed cost variable contribution margin safety' },
  { icon: '📈', title: 'Profit Margin Calculator',    cat: 'Finance',    href: '/services/profit-margin-calculator/',              tags: 'profit margin gross net operating ebit cogs opex markup pricing' },
  { icon: '🔄', title: 'Working Capital Calculator',  cat: 'Finance',    href: '/services/working-capital-calculator/',            tags: 'liquidity current assets liabilities current ratio quick ratio nwc' },
  { icon: '🛡️', title: 'DSCR Calculator',             cat: 'Finance',    href: '/services/dscr-calculator/',                       tags: 'debt service coverage lending' },
  // Developer Services
  { icon: 'JSON', title: 'JSON Editor & Formatter Online', cat: 'Developer', href: '/services/json-editor/', tags: 'json format validate prettify minify tree table compare diff repair query transform online editor', dev: true },
  { icon: 'B64',  title: 'Base64 Encoder & Decoder',  cat: 'Developer',  href: '/services/base64-encoder-decoder/',                 tags: 'base64 base64url encode decode jwt utf8 ascii latin1 text', dev: true },
  { icon: '.*',   title: 'RegEx Validator & Tester',          cat: 'Developer', href: '/services/regex-validator/',                tags: 'regex regular expression validator tester pattern match groups validate test email phone url', dev: true },
  { icon: 'JWT',  title: 'JWT Token Inspector & Debugger',    cat: 'Developer',  href: '/services/jwt-token-inspector/',                   tags: 'jwt json web token decode verify inspect debugger hmac rsa signature claims exp', dev: true },
  { icon: '⏰',   title: 'Cron Expression Parser & Schedule Generator', cat: 'Developer',  href: '/services/cron-expression-parser/', tags: 'cron schedule expression parser crontab quartz next run timestamp schedule interval', dev: true },
  { icon: '🔒',   title: 'Hash & Cipher Generator',   cat: 'Developer',  href: '/services/hash-cipher-generator/',                  tags: 'sha256 sha512 md5 sha1 ripemd160 crc32 hmac hash cipher encrypt decrypt aes identifier', dev: true },
];

function initGlobalSearch() {
  const input   = document.getElementById('globalSearchInput');
  const results = document.getElementById('globalSearchResults');
  if (!input || !results) return;

  let activeIdx = -1;
  let currentItems = [];

  function getItems() {
    return Array.from(results.querySelectorAll('.gsearch-item'));
  }

  function setActive(idx) {
    const items = getItems();
    items.forEach(el => el.classList.remove('is-active'));
    if (idx >= 0 && idx < items.length) {
      items[idx].classList.add('is-active');
      items[idx].scrollIntoView({ block: 'nearest' });
    }
    activeIdx = idx;
  }

  function renderResults(filtered) {
    results.hidden = false;
    results.innerHTML = '';

    if (!filtered.length) {
      const li = document.createElement('li');
      li.className = 'gsearch-empty';
      li.textContent = 'No utilities found';
      results.appendChild(li);
      activeIdx = -1;
      return;
    }

    // Group by category
    const finance   = filtered.filter(t => t.cat === 'Finance');
    const developer = filtered.filter(t => t.cat === 'Developer');

    function appendGroup(label, group) {
      if (!group.length) return;
      const heading = document.createElement('li');
      heading.className = 'gsearch-section-label';
      heading.textContent = label;
      results.appendChild(heading);

      group.forEach(tool => {
        const a = document.createElement('a');
        a.className = 'gsearch-item';
        a.href = tool.href;
        a.innerHTML = `
          <span class="gsearch-item-icon${tool.dev ? ' dev-icon' : ''}">${tool.icon}</span>
          <span class="gsearch-item-body">
            <span class="gsearch-item-title">${tool.title}</span>
            <span class="gsearch-item-cat">${tool.cat} utility</span>
          </span>`;
        a.addEventListener('click', () => {
          if (typeof window.gtagEvent === 'function') {
            window.gtagEvent('search_result_click', {
              search_term: input.value,
              result_title: tool.title,
              result_url: tool.href,
              category: tool.cat,
            });
          }
          results.hidden = true;
          input.value = '';
        });
        const li = document.createElement('li');
        li.appendChild(a);
        results.appendChild(li);
      });
    }

    appendGroup('💰 Finance', finance);
    appendGroup('💻 Developer', developer);
    activeIdx = -1;
  }

  function search(q) {
    const query = q.toLowerCase().trim();
    if (!query) { results.hidden = true; return; }
    const filtered = GLOBAL_TOOLS.filter(t =>
      t.title.toLowerCase().includes(query) ||
      t.cat.toLowerCase().includes(query)   ||
      t.tags.toLowerCase().includes(query)
    );
    renderResults(filtered);
  }

  input.addEventListener('input', e => search(e.target.value));

  input.addEventListener('focus', e => {
    if (e.target.value.trim()) search(e.target.value);
  });

  input.addEventListener('keydown', e => {
    const items = getItems();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(Math.min(activeIdx + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(Math.max(activeIdx - 1, 0));
    } else if (e.key === 'Enter') {
      if (activeIdx >= 0 && items[activeIdx]) {
        items[activeIdx].click();
      }
    } else if (e.key === 'Escape') {
      results.hidden = true;
      input.blur();
    }
  });

  // Close on outside click
  document.addEventListener('click', e => {
    const wrap = document.getElementById('globalSearchWrap');
    if (wrap && !wrap.contains(e.target)) {
      results.hidden = true;
    }
  });

  // Keyboard shortcut: / or Cmd+K to focus
  document.addEventListener('keydown', e => {
    if ((e.key === '/' || (e.key === 'k' && (e.metaKey || e.ctrlKey))) && document.activeElement !== input) {
      e.preventDefault();
      input.focus();
      input.select();
    }
  });
}

/* ─── Double-Click Value Editing & 1 Crore Capping Engine ─── */
const ONE_CRORE = 10000000;

function initDoubleClickValueEditing() {
  document.querySelectorAll('.field-value, span[id$="Display"]').forEach((span) => {
    if (span.dataset.dblclickBound) return;
    span.dataset.dblclickBound = 'true';
    span.title = 'Double-click to edit value directly';

    span.addEventListener('dblclick', (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Locate associated slider input element
      let inputId = span.id ? span.id.replace('Display', '') : '';
      let slider = document.getElementById(inputId);

      if (!slider) {
        const label = span.closest('label');
        if (label && label.getAttribute('for')) {
          slider = document.getElementById(label.getAttribute('for'));
        }
      }

      if (!slider || slider.type !== 'range') return;

      const currentVal = slider.value;
      const input = document.createElement('input');
      input.type = 'number';
      input.className = 'inline-value-edit';
      input.value = currentVal;
      if (slider.step && slider.step !== 'any') {
        input.step = slider.step;
      }

      const originalText = span.textContent;
      span.textContent = '';
      span.appendChild(input);
      input.focus();
      input.select();

      let finished = false;
      const finishEdit = () => {
        if (finished) return;
        finished = true;

        let num = parseFloat(input.value);
        if (isNaN(num)) {
          num = parseFloat(currentVal);
        }

        const sliderId = slider.id.toLowerCase();
        const isPercentageOrYears = sliderId.includes('rate') || sliderId.includes('tenure') || 
                                   sliderId.includes('years') || sliderId.includes('pct') || 
                                   sliderId.includes('growth') || sliderId.includes('return') ||
                                   sliderId.includes('appreciation') || sliderId.includes('maintenance') ||
                                   sliderId.includes('stepup');

        const FIVE_CRORE = 50000000;
        let maxCap = isPercentageOrYears ? parseFloat(slider.max || 100) : (sliderId === 'propertyprice' ? FIVE_CRORE : ONE_CRORE);

        // Cap value up to 5 Crore for propertyPrice / 1 Crore for other money inputs, or slider max for percentage/years
        num = Math.min(num, maxCap);
        num = Math.max(parseFloat(slider.min || 0), num);

        // Dynamically adjust slider max if input value exceeds current slider max
        if (num > parseFloat(slider.max)) {
          slider.max = Math.min(maxCap, Math.max(num, parseFloat(slider.max)));
        }

        slider.value = num;
        slider.dispatchEvent(new Event('input', { bubbles: true }));
        slider.dispatchEvent(new Event('change', { bubbles: true }));

        if (typeof window.gtagEvent === 'function') {
          window.gtagEvent('inline_edit_value', {
            slider_id: slider.id,
            new_value: num,
          });
        }
      };

      input.addEventListener('keydown', (evt) => {
        if (evt.key === 'Enter') {
          finishEdit();
        } else if (evt.key === 'Escape') {
          finished = true;
          span.textContent = originalText;
        }
      });

      input.addEventListener('blur', finishEdit);
    });
  });
}

function initCountry(onChanged) {
  if (onChanged) onCountryChangeListeners.push(onChanged);
  populateCountrySelect();
  initI18n();

  let saved = null;
  try {
    saved = localStorage.getItem(STORAGE_KEY);
  } catch (e) {}

  if (saved && COUNTRIES[saved]) {
    setCountry(saved, { persist: false });
  } else {
    const { id } = detectCountryFromLocale();
    setCountry(id, { persist: false });
  }

  const select = document.getElementById('countrySelect');
  if (select) {
    select.addEventListener('change', (e) => {
      setCountry(e.target.value);
    });
  }
}

function initI18n() {
  populateLanguageSelect();
  setLanguage(getLanguage(), { persist: false });
}

document.addEventListener('DOMContentLoaded', () => {
  initTheme();
  initI18n();
  renderBreadcrumbs();
  initSearchableDropdowns();
  initGlobalSearch();
  initDoubleClickValueEditing();
});


